import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase-types";
import type {
  SummariesResponse,
  QuestionsResponse,
} from "@/types/pocketbase-types";
import type { IArticle, Question, Answer } from "@/types/extended-types";

import Quiz from "@/components/application/quiz/Quiz";
import LoadingSpinner from "@/components/LoadingSpinner";

import sports from "@/assets/imgs/sports-bg-4.jpg";
import science from "@/assets/imgs/science-bg.svg";
import world from "@/assets/imgs/world-bg-2.svg";
import tech from "@/assets/imgs/tech-bg.png";
import business from "@/assets/imgs/business-bg-3.jpg";

interface ArticleDetailsProps {
  article: IArticle;
  summaryLevel: string | undefined;
}

interface QuizSubmissionData {
  answers: Answer[];
  summaryId: string;
  timeSpent: number;
}
export const queryKeys = {
  articles: ["articles"] as const,
} as const;

const ArticleDetails = ({ article, summaryLevel }: ArticleDetailsProps) => {
  const [quizStarted, setQuizStarted] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | undefined>(undefined);
  const [bannerStyle, setBannerStyle] = useState<React.CSSProperties>({});
  const [tagStyle, setTagStyle] = useState<React.CSSProperties>({});
  // const [summaryLevel, setSummaryLevel] = useState(summaryLevelProp);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const readingStatus = article.quiz_taken ?? 0; // 0: 2 tries left, 1: 1 try left, 2: 0 tries left

  // Get percentage from enriched article data (0-100)
  const percentage = article.percentage ?? 0;

  const btnStyle = {
    backgroundColor:
      readingStatus === 0
        ? "var(--primary)"
        : readingStatus === 1
        ? "var(--yellow)"
        : "var(--disabled)",
    color: "var(--white)",
  };

  // Fetch article summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["article-summary", article.id],
    queryFn: async (): Promise<SummariesResponse> => {
      const record = await pb
        .collection(Collections.Summaries)
        .getFirstListItem(`source="${article.id}"`);
      return record as unknown as SummariesResponse;
    },
    enabled: !!article.id,
  });

  // Fetch quiz questions
  const { data: questions = [], isLoading: questionsLoading } = useQuery({
    queryKey: ["article-questions", summary?.id, summaryLevel],
    queryFn: async (): Promise<Question[]> => {
      if (!summary?.id) return [];
      let lexical = summaryLevel ? summaryLevel.toUpperCase() : "BEGINNER";

      // Fetch questions with same logic as Next.js
      const records = (await pb
        .collection(Collections.Questions)
        .getList(1, 50, {
          filter: `summary="${summary.id}" && lexical="${lexical}"`,
          sort: "@random",
        })) as unknown as { items: QuestionsResponse[] };

      // Separate LO and MCQ questions
      const loQuestions = records.items
        .filter((q) => q.type === "LO")
        .slice(0, 0);
      const mcqQuestions = records.items
        .filter((q) => q.type === "MCQ")
        .slice(0, 7);

      // Combine to get exactly 7 questions (0 LO + 7 MCQ)
      const limitedQuestions = [...loQuestions, ...mcqQuestions];

      return limitedQuestions.map((q) => ({
        id: q.id,
        question: q.question || "",
        type: q.type as "MCQ" | "LO",
        options: q.type === "MCQ" && q.choices ? (q.choices as any) : undefined,
        choices: q.choices as string[] | null,
      }));
    },
    enabled: !!summary?.id,
  });

  // Quiz submission mutation
  const quizSubmissionMutation = useMutation({
    mutationFn: async (data: QuizSubmissionData) => {
      const response = await fetch(`${pb.baseUrl}/api/quiz/article/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to submit quiz");
      }

      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["articles"] });
      await queryClient.invalidateQueries({
        queryKey: ["article", article.id],
      });
      // Invalidate the quiz score query to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: ["article-quiz-score", article.id]
      });
      // Invalidate user activities to update profile quiz history
      await queryClient.invalidateQueries({
        queryKey: ["userActivities"]
      });

      // Show success message
      enqueueSnackbar("Quiz submitted successfully!", { variant: "success" });

      // Increased delay to ensure database write completes before navigation
      setTimeout(() => {
        navigate({
          to: "/articles/quiz-results/$slug",
          params: { slug: article.id },
          search: summaryLevel ? { summaryLevel } : undefined,
        });
      }, 500);
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.message || "Failed to submit quiz. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleStartQuiz = () => {
    setQuizStarted(true);
    enqueueSnackbar("Quiz started! Good luck!", { variant: "info" });
  };

  const handleQuizComplete = (answers: Answer[], timeSpentSeconds: number) => {
    if (!summary?.id) return;

    quizSubmissionMutation.mutate({
      answers,
      summaryId: summary.id,
      timeSpent: timeSpentSeconds,
    });
  };

  const handleCancelQuiz = () => {
    setQuizStarted(false);
  };

  useEffect(() => {
    switch (article.expand?.section?.name) {
      case "sports":
        setBannerImage(sports);
        setTagStyle({ backgroundColor: "var(--dark-blue)" });
        setBannerStyle({ color: "var(--white)" });
        break;
      case "science":
        setBannerImage(science);
        setTagStyle({
          backgroundColor: "var(--white)",
          color: "var(--dark-blue)",
        });
        setBannerStyle({ color: "var(--white)" });
        break;
      case "world":
        setBannerImage(world);
        setTagStyle({ backgroundColor: "var(--dark-blue)" });
        setBannerStyle({ color: "var(--white)" });
        break;
      case "technology":
        setBannerImage(tech);
        setTagStyle({ backgroundColor: "var(--white)", color: "#1849a9" });
        setBannerStyle({
          color: "var(--white)",
          backgroundColor: "#1849a9",
        });
        break;
      case "business":
        setBannerImage(business);
        setTagStyle({
          backgroundColor: "var(--primary)",
          color: "var(--white)",
        });
        setBannerStyle({
          color: "var(--white)",
        });
        break;
      default:
        break;
    }
  }, []);

  if (summaryLoading) {
    return (
      <LoadingSpinner
        text="Loading article content..."
        className="min-h-[400px]"
      />
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-12">
        <div className="text-error-500 text-6xl mb-4">📰</div>
        <h2 className="text-xl font-bold text-error-500 mb-2">
          Article content not found
        </h2>
        <p className="text-neutral-500">
          The article summary could not be loaded.
        </p>
      </div>
    );
  }

  // Show article content and quiz start
  return (
    <div className="container mx-auto">
      <div className="mt-20 md:p-12 p-6 md:max-w-[1440px] w-full mx-auto flex flex-col gap-y-7 min-h-screen">
        {/* <div className="article-level-switcher">
          <button
            className={`level-switcher-btn ${
              summaryLevel == "beginner" && "active"
            }`}
            onClick={() => handleSummaryLevelChange("beginner")}
          >
            Beginner
          </button>
          <button
            className={`level-switcher-btn ${
              summaryLevel == "intermediate" && "active"
            }`}
            onClick={() => handleSummaryLevelChange("intermediate")}
          >
            Intermediate
          </button>
          <button
            className={`level-switcher-btn ${
              summaryLevel == "advanced" && "active"
            }`}
            onClick={() => handleSummaryLevelChange("advanced")}
          >
            Advanced
          </button>
        </div> */}
        <div className="article-details-header" style={bannerStyle}>
          <img src={bannerImage} alt="" />
          <div className="article-details-header__text flex flex-col gap-y-2">
            <span className="article-details-header__subtitle" style={tagStyle}>
              {article.expand?.section?.name || "Article"}
            </span>
            <h1 className="mt-3">{article.title}</h1>
            <span className="article-details-header__subtext">
              You can take the quiz only 2 times - {readingStatus} / 2
            </span>
          </div>
        </div>

        <div className="md:p-12 p-0 md:max-w-[1440px] w-full mx-auto">
          <p className="md:leading-relaxed leading-relaxed text-justify article-text-content">
            {/* {summary.intermediate || article.abstract} */}

            {(summaryLevel && (summary as any)[summaryLevel]) ||
              article.abstract}
          </p>
        </div>
        {!quizStarted && (
          <div className="z-10 gap-y-[10px] md:p-12 p-0">
            {readingStatus >= 2 ? ( // No tries left - button disabled
              <button
                className="article-take-quiz-button disabled-btn"
                style={btnStyle}
              >
                {"You have no tries left - Your final score: " + percentage.toFixed(2) + "%"}
              </button>
            ) : (
              <button
                onClick={handleStartQuiz}
                className="article-take-quiz-button"
                style={btnStyle}
              >
                {"Ready for the challenge? Take a quiz on what you read - " +
                  readingStatus +
                  "/ 2"}
              </button>
            )}
          </div>
        )}

        {quizStarted &&
          (questionsLoading ? (
            <LoadingSpinner
              text="Loading quiz questions..."
              className="min-h-[400px]"
            />
          ) : (
            <Quiz
              questions={questions}
              onComplete={handleQuizComplete}
              onCancel={handleCancelQuiz}
            />
          ))}
      </div>
    </div>
  );
};

export default ArticleDetails;
